import { DecimalPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';

import { ContentCardComponent } from '../../../../../shared/components/content-card/content-card.component';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { CpfCnpjPipe } from '../../../../../shared/pipes/cpf-cnpj.pipe';
import { TipoPessoaEnum } from '../../../../../shared/enums/pessoa/tipo-pessoa.enum';
import { PessoaLookupService } from '../../../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { ClienteDetalheStruct } from '../../../../../shared/services/structs/vendas/cliente-detalhe.struct';
import { HistoricoClienteStruct } from '../../../../../shared/services/structs/vendas/historico-cliente.struct';
import { KanbanCor } from '../../../../../shared/services/models/vendas/kanban-cor.model';
import { ClienteVendaSelectStruct } from '../../../../../shared/services/structs/pessoa/cliente-venda-select.struct';
import { VendaDocumentoService } from '../../../../../shared/services/apis/vendas/venda-documento.service';
import { VdClienteBuscaDialogComponent } from './dialogs/vd-cliente-busca-dialog/vd-cliente-busca-dialog.component';
import {
    AbaClienteDialog,
    VdClienteDialogComponent,
    VdClienteDialogData,
} from './dialogs/vd-cliente-dialog/vd-cliente-dialog.component';

/**
 * Campo Cliente (§5.1) — dois estados, e não um select.
 *
 *   VAZIO ......... campo de busca com a lupa da pesquisa avançada ao lado, os links de atalho
 *                   abaixo, e o dropdown com avatar, nome, documento e o badge do tipo
 *   SELECIONADO ... o CARD: ícone, nome, documento, localidade, o chip de limite disponível e
 *                   o botão "Trocar"
 *
 * A lista ABRE AO FOCAR, sem digitar: exigir que o usuário digite para ver qualquer coisa é
 * pedir que ele adivinhe o que existe cadastrado.
 *
 * O `select-pesquisavel` genérico não serve aqui: ele mostra o escolhido como texto numa caixa,
 * e o cliente escolhido vira um cartão com dados de outros serviços. Antes de escolher a
 * pergunta é "quem é?"; depois, "com quem estou lidando?".
 */
@Component({
    selector: 'app-vd-cliente',
    standalone: true,
    imports: [
        ContentCardComponent,
        AbntPipe,
        DecimalPipe,
        CpfCnpjPipe,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './vd-cliente.component.html',
    styleUrl: './vd-cliente.component.css',
})
export class VdClienteComponent implements OnInit, OnChanges {
    @Input({ required: true }) public control!: FormControl;
    @Input() public isSomenteLeitura: boolean = false;

    /** O cliente já gravado, para o card abrir preenchido em vez de mostrar a busca. */
    @Input() public clienteSelecionado: ClienteVendaSelectStruct | null = null;

    /**
     * Cidade/UF, limite disponível e atraso, resolvidos pelo orquestrador ao selecionar. Ficam
     * fora do struct da busca porque vêm de três serviços — e o dropdown não precisa deles.
     */
    @Input() public localidade: string | null = null;
    @Input() public limiteDisponivel: number | null = null;
    @Input() public temTitulosEmAtraso: boolean = false;
    @Input() public isCarregandoDadosDoCliente: boolean = false;

    /** As cores do kanban, para o modal pintar a etapa. Vem do agregado de configuracoes. */
    @Input() public coresKanban: KanbanCor[] = [];

    /**
     * Se o documento em edicao RECUSA cliente com titulo vencido. Vem de cima porque depende do
     * TIPO do documento e da configuracao da empresa — o campo de cliente nao conhece nenhum dos
     * dois, e nao deveria.
     */
    @Input() public bloqueiaPorAtraso: boolean = false;

    /** Cliente que não pode ser trocado (documento ligado a uma oportunidade do CRM): o motivo vai no tooltip do Trocar. */
    @Input() public motivoClienteBloqueado: string | null = null;

    @Output() public clienteAlterado = new EventEmitter<ClienteVendaSelectStruct | null>();
    /** O card completo, para quem precisar do limite adiante — a validação de bloqueio, por ex. */
    @Output() public dadosFinanceiros = new EventEmitter<ClienteDetalheStruct>();

    /**
     * As últimas compras do cliente, para a tag "1ª Venda". Nulo enquanto carrega, sem cliente ou quando a consulta
     * falhou; lista vazia é cliente sem histórico.
     */
    @Output() public historicoCliente = new EventEmitter<HistoricoClienteStruct | null>();

    @Output() public pesquisaAvancada = new EventEmitter<void>();


    public termo: string = '';
    public resultados: ClienteVendaSelectStruct[] = [];
    public isBuscando: boolean = false;
    public isDropdownAberto: boolean = false;

    private readonly digitou$ = new Subject<string>();

    /** Evita repetir a consulta do financeiro a cada ciclo de detecção de mudanças. */
    private idComFinanceiroCarregado: number | null = null;

    /**
     * O cliente inteiro, como veio do servidor — o card mostra uma parte e o modal recebe o
     * resto. Guardar aqui e o que faz o modal abrir sem carregar nada.
     */
    private clienteCompleto: ClienteDetalheStruct | null = null;
    private historico: HistoricoClienteStruct | null = null;

    constructor(
        private dialog: MatDialog,
        private pessoaLookupService: PessoaLookupService,
        private vendaDocumentoService: VendaDocumentoService,
    ) {}

    /**
     * Os atalhos só abrem depois que o financeiro respondeu: clicar antes abriria um modal que
     * ainda não sabe dizer se o cliente pode receber o pedido.
     */
    public get podeAbrirDetalhe(): boolean {
        return this.clienteCompleto != null && !this.isCarregandoDadosDoCliente;
    }

    public abrirDetalhe(aba: AbaClienteDialog): void {
        if (!this.podeAbrirDetalhe) return;

        this.dialog.open(VdClienteDialogComponent, {
            width: '860px',
            maxWidth: '96vw',
            autoFocus: false,
            data: {
                cliente: this.clienteCompleto!,
                historico: this.historico,
                cores: this.coresKanban,
                aba,
                bloqueiaPorAtraso: this.bloqueiaPorAtraso,
            } as VdClienteDialogData,
        });
    }

    /**
     * A lupa abre a pesquisa de cadastro (§5.1) — a caçada, com refino por campo e paginação.
     * O que ela devolve entra pelo mesmo caminho de uma escolha no autocomplete.
     */
    public abrirPesquisaAvancada(): void {
        this.pesquisaAvancada.emit();

        this.dialog
            .open(VdClienteBuscaDialogComponent, { width: '840px', maxWidth: '96vw', autoFocus: false })
            .afterClosed()
            .subscribe((escolhido?: ClienteVendaSelectStruct) => {
                if (escolhido) this.escolher(escolhido);
            });
    }

    public ngOnInit(): void {
        // debounce porque a busca é ONLINE: sem ele, cada tecla vira uma requisição à API.Pessoa.
        this.digitou$
            .pipe(
                debounceTime(350),
                distinctUntilChanged(),
                switchMap((texto) => {
                    this.isBuscando = true;
                    return this.pessoaLookupService.pesquisarClienteVenda(texto);
                }),
            )
            .subscribe({
                next: (res) => {
                    this.resultados = res.isError ? [] : res.list ?? [];
                    this.isBuscando = false;
                },
                error: () => {
                    this.resultados = [];
                    this.isBuscando = false;
                },
            });

        // Carrega a lista JÁ na abertura da tela, e não ao focar: quando o vendedor clica no
        // campo, os primeiros clientes têm de estar lá. Buscar só no foco gasta o tempo dele
        // esperando o que poderia ter vindo enquanto ele lia o resto do documento.
        this.digitou$.next('');
    }

    public ngOnChanges(): void {
        // Documento carregado com cliente: o card abre preenchido, mas o financeiro ainda não foi
        // consultado — e é ele que diz se o cliente está em atraso.
        const id = this.clienteSelecionado?.idPessoa;

        if (id != null && id !== this.idComFinanceiroCarregado) {
            this.idComFinanceiroCarregado = id;
            this.carregarDadosDoCliente(id);
        }
    }

    /**
     * Abre a lista, que já veio carregada na abertura da tela. A rebusca só acontece se a
     * primeira falhou — do contrário seria uma requisição por clique no campo.
     */
    public aoFocar(): void {
        this.isDropdownAberto = true;

        if (this.resultados.length === 0 && !this.isBuscando) this.digitou$.next('');
    }

    public aoDigitar(valor: string): void {
        this.termo = valor;
        this.isDropdownAberto = true;
        this.digitou$.next(valor);
    }

    public escolher(item: ClienteVendaSelectStruct): void {
        this.clienteSelecionado = item;
        this.control.setValue(item.idPessoa);
        this.control.markAsDirty();
        this.isDropdownAberto = false;
        this.termo = '';
        this.idComFinanceiroCarregado = item.idPessoa;
        this.clienteAlterado.emit(item);
        this.carregarDadosDoCliente(item.idPessoa);
    }

    /**
     * O financeiro do cliente, buscado ao ESCOLHER. O card já aparece com nome e documento — que
     * vieram da busca — e o chip chega depois: esperar o financeiro para mostrar o nome deixaria
     * a tela parada por um dado que é complemento.
     */
    private carregarDadosDoCliente(idPessoa: number): void {
        this.isCarregandoDadosDoCliente = true;
        this.historicoCliente.emit(null);

        this.vendaDocumentoService.getCliente(idPessoa).subscribe({
            next: (res) => {
                this.isCarregandoDadosDoCliente = false;

                if (res.isError || !res.cliente) return;

                this.clienteCompleto = res.cliente;
                this.historico = res.historico;

                this.localidade = res.cliente.localidade;
                this.limiteDisponivel = res.cliente.limiteDisponivel;
                this.temTitulosEmAtraso = res.cliente.temTitulosEmAtraso;
                this.dadosFinanceiros.emit(res.cliente);
                this.historicoCliente.emit(res.historico);
            },
            error: () => {
                this.isCarregandoDadosDoCliente = false;
            },
        });
    }

    /** "Trocar" volta à busca — e limpa o id, porque o documento fica sem cliente. */
    public trocar(): void {
        this.clienteSelecionado = null;
        this.clienteCompleto = null;
        this.historico = null;
        this.localidade = null;
        this.limiteDisponivel = null;
        this.temTitulosEmAtraso = false;
        this.idComFinanceiroCarregado = null;
        this.control.setValue(null);
        this.control.markAsDirty();
        this.clienteAlterado.emit(null);
        this.historicoCliente.emit(null);
    }

    /**
     * O dropdown fecha no blur, mas com atraso: sem ele o blur acontece ANTES do clique no
     * resultado, e o item escolhido nunca chega.
     */
    public fecharDepois(): void {
        setTimeout(() => (this.isDropdownAberto = false), 180);
    }

    public inicialDe(item: ClienteVendaSelectStruct): string {
        return (item.nome ?? '?').trim().charAt(0).toUpperCase();
    }

    public rotuloDoTipo(item: ClienteVendaSelectStruct): string {
        switch (item.idTipoPessoa) {
            case TipoPessoaEnum.Fisica:
                return 'Física';
            case TipoPessoaEnum.Juridica:
                return 'Jurídica';
            case TipoPessoaEnum.Estrangeiro:
                return 'Estrangeiro';
            default:
                return '';
        }
    }
}
